import React from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '../../../../components/ui/SelectField';
import { useGetCustomerClassesQuery } from '../../../../api/organization/organizationApiSlice';
import {
    NO_CLASS_VALUE,
    buildCustomerClassOptions,
    hasCustomerClassChoice,
    withInheritedClassLabel,
} from '../../../organizations/organizationSectionVisibility';

interface BookingCustomerClassSelectProps {
    /** The selected class id, or null for "no override". */
    value: string | null;
    onChange: (customerClassId: string | null) => void;
    disabled?: boolean;
    /**
     * The class "no override" would price the booking under, so the entry is
     * named after it rather than after the system default. On create: the
     * class the booking inherits from its organisation (unit), #1810. On
     * edit: the class a cleared override applies, from the backend's edit
     * resolver (`noOverrideCustomerClassId`).
     */
    inheritedClassId?: string | null;
}

/**
 * The customer class this booking is priced under (#1808) — resolution step 1,
 * the operator's per-booking override.
 *
 * ## It reuses the organization option list on purpose
 *
 * `buildCustomerClassOptions` offers "no override" under the system default's
 * NAME rather than as a separate entry, because both resolve to the same
 * tariff and offering two spellings of one answer is what the 2026-08-19
 * staging pass tripped over. The stored value stays `null`, so a booking that
 * inherits keeps inheriting if the tenant later renames its default.
 *
 * That holds only while "no override" resolves to the system default. When
 * it resolves to another class, `inheritedClassId` renames the entry after
 * that class. On create that is an attributed organisation's (unit's) class.
 * On edit it is whatever a cleared override applies: a web booking's own
 * member or covering class, or a staff booking's account/organisation chain.
 * The caller passes the class the backend will actually price by, so the
 * label never names a different class. (Units are the documented exception:
 * there `null` means "inherit from the org", so they build their own list.)
 *
 * ## Hidden when there is nothing to choose
 *
 * Unlike the organization field, this one HIDES with a single class rather
 * than showing a hint. An organization's class is a lasting property worth
 * advertising as configurable; a booking form is a task the operator is trying
 * to finish, and a select with one option is a step that cannot change
 * anything. The place to discover the axis is Customers → Customer classes,
 * which is where it gets configured.
 *
 * A legacy tenant gets a 409 from the endpoint — customer classes are an
 * operational-schema concept — so `classes` stays empty and the field is
 * absent, which is correct rather than merely convenient.
 */
const BookingCustomerClassSelect: React.FC<BookingCustomerClassSelectProps> = ({
    value,
    onChange,
    disabled = false,
    inheritedClassId = null,
}) => {
    const { t } = useTranslation();
    const { data: classes = [], isLoading } = useGetCustomerClassesQuery();

    const hasChoice = React.useMemo(() => hasCustomerClassChoice(classes), [classes]);

    const { values, labels } = React.useMemo(
        () =>
            // An attributed booking with no override is priced by its
            // organisation's class, not the system default (#1810).
            withInheritedClassLabel(
                buildCustomerClassOptions(classes, t('organization.fields.customer_class_none')),
                classes,
                inheritedClassId,
            ),
        [classes, t, inheritedClassId],
    );

    if (isLoading || !hasChoice) return null;

    return (
        <SelectField
            labelAbove={true}
            label={t('create_booking.reservation_details.customer_class')}
            help={t('create_booking.reservation_details.customer_class_help')}
            value={value ?? NO_CLASS_VALUE}
            onChange={(val) => onChange(val === NO_CLASS_VALUE ? null : val)}
            options={values}
            optionLabels={labels}
            disabled={disabled}
        />
    );
};

export default BookingCustomerClassSelect;
